import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import {
    ClientLink,
    ClientLinkGetPayload,
    ClientLinkPostPayloadType,
    ClientLinkPostResponse,
    ClientLinkPutPayload
} from 'application/Shared/types/clientLink';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

interface ClientLinkGetResponseData {
    data: ClientLink[];
    message: string;
    serverResponse: number;
}

interface ClientLinkPostResponseData {
    data: ClientLinkPostResponse;
    message: string;
    serverResponse: number;
}

export const useGetClientLinks = (params: ClientLinkGetPayload) => {
    const [clientLinks, setClientLinks] = useState<ClientLink[]>([]);
    const [loading, setLoading] = useState(false);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => setShouldRefetch({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axios.get<ClientLinkGetResponseData>(
                    '/clientLink',
                    {
                        cancelToken: source.token,
                        params
                    }
                );

                if (Response.hasData(response)) {
                    const clientLinksData = Response.getData(response);
                    setClientLinks(clientLinksData);
                }
            } catch (e) {
                debug.e('Unable to load client links', e);
            }
            setLoading(false);
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [params, shouldRefetchData]);

    return { clientLinks, loading, refetchData };
};

export const usePutClientLink = () => {
    const [saving, setSaving] = useState(false);

    const putClientLink = useCallback(
        async ({
            id,
            params
        }: {
            id: string;
            params: ClientLinkPutPayload;
        }) => {
            try {
                setSaving(true);
                await axios.put(`/clientLink/${id}`, params);
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e('Unable to put clientLink', err);
                setSaving(false);
                return false; // success = false
            }
        },
        []
    );

    return { putClientLink, saving };
};

export const usePostClientLink = () => {
    const [saving, setSaving] = useState(false);
    const [linkedTenantId, setLinkedTenantId] = useState<string | null>(null);

    const postClientLink = useCallback(
        async (params: ClientLinkPostPayloadType) => {
            try {
                debug.dev('Creating New Client', params);

                setSaving(true);
                const response = await axios.post<ClientLinkPostResponseData>(
                    '/clientLink',
                    params
                );

                const data = Response.getData(response);
                setLinkedTenantId(data.linkedTenantId);

                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e('Unable to post clientLink', err);
                setSaving(false);
                return false; // success = false
            }
        },
        []
    );

    return { linkedTenantId, postClientLink, saving };
};
